/**
 * Image URL resolver.
 *
 * Converts various image URL formats to publicly accessible HTTP URLs.
 * Handles:
 *   - gs://bucket/path → Firebase Storage public URL
 *   - Already-http URLs → pass through
 *   - Null/empty → null
 */

const FIREBASE_STORAGE_BASE = 'https://firebasestorage.googleapis.com/v0/b'

/**
 * Resolve an image source to a loadable HTTP URL.
 * @param {string|object|null} image — raw image URL string, or image object with .url
 * @returns {string|null} — public HTTP URL or null
 */
export function resolveImageUrl(image) {
  if (!image) return null

  // Handle image objects (from our Item.images[] schema)
  const raw = typeof image === 'string' ? image : image?.url || image?.storagePath || null
  if (!raw) return null

  // Already a public URL
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw

  // gs:// URL → Firebase Storage public URL
  if (raw.startsWith('gs://')) {
    const match = raw.match(/^gs:\/\/([^/]+)\/(.+)$/)
    if (match) {
      const [, bucket, path] = match
      const encodedPath = path.split('/').map(encodeURIComponent).join('%2F')
      return `${FIREBASE_STORAGE_BASE}/${bucket}/o/${encodedPath}?alt=media`
    }
  }

  // Bare path (no protocol) — assume it's a Firebase Storage path
  if (raw.startsWith('/') || raw.startsWith('images/')) {
    const bucket = 'indian-heritage-gallery-bucket'
    const cleanPath = raw.startsWith('/') ? raw.slice(1) : raw
    const encodedPath = cleanPath.split('/').map(encodeURIComponent).join('%2F')
    return `${FIREBASE_STORAGE_BASE}/${bucket}/o/${encodedPath}?alt=media`
  }

  // Fallback — return as-is
  return raw
}

/**
 * Get the first displayable image URL from an item's images array.
 * @param {Array} images — item.images array
 * @returns {string|null}
 */
export function getItemThumbnail(images) {
  if (!images || images.length === 0) return null
  return resolveImageUrl(images[0])
}

export default { resolveImageUrl, getItemThumbnail }
